import { useEffect, useState } from "react";
import { Car, FolderOpen, Search, UserRound } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Pill } from "@/components/common";
import { api } from "@/lib/api";
import { fmtKm } from "@/lib/format";
import { STATUS_MAP } from "@/lib/i18n";

export function ReceptionLookup({ onVehicle, onClient, onRepairOrder }) {
  const [q, setQ] = useState("");
  const [res, setRes] = useState(null);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    if (q.trim().length < 2) { setRes(null); return; }
    let alive = true;
    setLoading(true);
    const t = setTimeout(() => api.get("/search/reception", { params: { q } }).then((r) => alive && setRes(r.data)).catch(() => alive && setRes(null)).finally(() => alive && setLoading(false)), 250);
    return () => { alive = false; clearTimeout(t); };
  }, [q]);
  const empty = res && !res.vehicles.length && !res.clients.length && !res.repair_orders.length;
  return (
    <div className="space-y-3" data-testid="reception-lookup">
      <div className="relative">
        <Search className="pointer-events-none absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-muted-foreground" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} autoFocus placeholder="Plaque, VIN, nom, téléphone ou n° de dossier…" className="h-14 rounded-2xl pl-12 text-base" data-testid="reception-lookup-input" />
      </div>
      {loading && <p className="text-xs text-muted-foreground">Recherche…</p>}
      {!loading && empty && <p className="text-sm text-muted-foreground" data-testid="reception-lookup-empty">Aucun véhicule, client ni dossier ne correspond : créez le client et le véhicule ci-dessous.</p>}
      {res && !loading && (
        <div className="grid gap-2 sm:grid-cols-2">
          {res.vehicles.map((v) => (
            <button type="button" key={v.id} onClick={() => onVehicle(v)} className="surface-hover flex items-start gap-3 rounded-xl border border-border p-3 text-left" data-testid={`lookup-vehicle-${v.plate}`}>
              <Car className="mt-0.5 h-5 w-5 shrink-0 text-primary" />
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2"><span className="rounded-md bg-secondary px-2 py-0.5 font-mono text-xs font-bold">{v.plate}</span><span className="font-semibold">{v.brand} {v.model}</span>{v.year && <span className="text-xs text-muted-foreground">{v.year}</span>}</div>
                <div className="text-xs text-muted-foreground">{v.client_name || "Sans client"}{v.client_phone ? ` · ${v.client_phone}` : ""} · {fmtKm(v.last_mileage)} · {v.history_count} dossier(s)</div>
                {v.open_ro && <Pill className={`mt-1 ${STATUS_MAP[v.open_ro.status]?.cls || ""}`}>Dossier ouvert {v.open_ro.number}</Pill>}
              </div>
            </button>
          ))}
          {res.clients.map((c) => (
            <button type="button" key={c.id} onClick={() => onClient(c)} className="surface-hover flex items-start gap-3 rounded-xl border border-border p-3 text-left" data-testid={`lookup-client-${c.id}`}>
              <UserRound className="mt-0.5 h-5 w-5 shrink-0 text-muted-foreground" />
              <div><div className="font-semibold">{c.display_name}</div><div className="text-xs text-muted-foreground">{c.phone || c.email || "—"} · {c.vehicle_count} véhicule(s) · {c.type === "entreprise" ? "Professionnel" : "Particulier"}</div></div>
            </button>
          ))}
          {res.repair_orders.map((r) => (
            <button type="button" key={r.id} onClick={() => onRepairOrder(r)} className="surface-hover flex items-start gap-3 rounded-xl border border-border p-3 text-left" data-testid={`lookup-ro-${r.number}`}>
              <FolderOpen className="mt-0.5 h-5 w-5 shrink-0 text-amber-500" />
              <div><div className="font-semibold">{r.number} · {r.plate}</div><div className="text-xs text-muted-foreground">{r.client_name} · {STATUS_MAP[r.status]?.label || r.status} · {r.reason}</div></div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
